import React, { useEffect, useState } from 'react';
import { ArrowDown, ArrowLeft, ArrowUp, Check, FileText, Plus, Trash2 } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { listTutorialDefinitions, TutorialDefinition, updateTutorialDefinitionContent } from '../api/services';
import PdfPreviewModal from '../components/PdfPreviewModal';
import './TutorialEditor.css';

type EditorStep = {
  instruction: string;
  targetText: string;
  sourcePage?: number;
  startTime?: number;
  endTime?: number;
  images: Array<{ pageNumber: number; name: string; dataUrl: string }>;
};

function formatVideoTime(seconds: number) {
  const whole = Math.floor(seconds);
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
}

function toEditorSteps(definition: TutorialDefinition): EditorStep[] {
  return definition.steps.map((step) => ({
    instruction: step.instruction,
    targetText: step.target ? JSON.stringify(step.target, null, 2) : '',
    sourcePage: step.sourcePage,
    startTime: step.startTime,
    endTime: step.endTime,
    images: step.images || [],
  }));
}

export default function TutorialEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [definition, setDefinition] = useState<TutorialDefinition | null>(null);
  const [title, setTitle] = useState('');
  const [tutorialKey, setTutorialKey] = useState('');
  const [appName, setAppName] = useState('');
  const [steps, setSteps] = useState<EditorStep[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saveMessage, setSaveMessage] = useState('');
  const [showPdf, setShowPdf] = useState(false);

  useEffect(() => {
    if (!id) {
      setError('No tutorial was selected.');
      setLoading(false);
      return;
    }

    void listTutorialDefinitions().then((definitions) => {
      const selected = definitions.find((item) => item.id === id);
      if (!selected) {
        setError('Tutorial draft not found.');
        return;
      }
      setDefinition(selected);
      setTitle(selected.title);
      setTutorialKey(selected.tutorialKey);
      setAppName(selected.appName);
      setSteps(toEditorSteps(selected));
    }).catch(() => {
      setError('Unable to load this tutorial draft.');
    }).finally(() => setLoading(false));
  }, [id]);

  const updateStep = (index: number, update: Partial<EditorStep>) => {
    setSteps((current) => current.map((step, stepIndex) => stepIndex === index ? { ...step, ...update } : step));
  };

  const moveStep = (index: number, direction: -1 | 1) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= steps.length) return;
    setSteps((current) => {
      const next = [...current];
      [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
      return next;
    });
  };

  const saveDraft = async () => {
    if (!id || !definition) return;
    setSaving(true);
    setError('');
    setSaveMessage('');
    try {
      const parsedSteps = steps.map((step, index) => {
        let target: Record<string, unknown> | undefined;
        if (step.targetText.trim()) {
          const parsed: unknown = JSON.parse(step.targetText);
          if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error(`Step ${index + 1} target must be a JSON object.`);
          target = parsed as Record<string, unknown>;
        }
        if (!step.instruction.trim()) throw new Error(`Step ${index + 1} needs an instruction.`);
        return { stepIndex: index, instruction: step.instruction.trim(), target, sourcePage: step.sourcePage, startTime: step.startTime, endTime: step.endTime };
      });
      const updated = await updateTutorialDefinitionContent(id, { title: title.trim(), tutorialKey: tutorialKey.trim(), appName: appName.trim(), steps: parsedSteps });
      setDefinition(updated);
      setSteps(toEditorSteps(updated));
      setSaveMessage('Draft saved. It is still unpublished.');
    } catch (saveError) {
      setError(saveError instanceof SyntaxError ? 'Target metadata must be valid JSON.' : saveError instanceof Error ? saveError.message : 'Unable to save this tutorial draft.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="tutorial-editor-page"><div className="tutorial-editor-state">Loading tutorial draft...</div></div>;
  if (error && !definition) return <div className="tutorial-editor-page"><div className="tutorial-editor-state"><strong>{error}</strong><Link className="btn btn-secondary" to="/tutorials"><ArrowLeft size={15} /> Back to tutorials</Link></div></div>;
  if (!definition) return null;
  if (definition.status !== 'draft') return <div className="tutorial-editor-page"><div className="tutorial-editor-state"><strong>Only draft tutorials can be edited.</strong><Link className="btn btn-secondary" to="/tutorials"><ArrowLeft size={15} /> Back to tutorials</Link></div></div>;

  return (
    <div className="tutorial-editor-page">
      <div className="tutorial-editor-header">
        <div>
          <Link className="tutorial-back-link" to="/tutorials"><ArrowLeft size={15} /> Tutorials</Link>
          <div className="page-header"><h2>Edit tutorial draft</h2><p>Configure the steps that will be delivered to the desktop app when this tutorial is published.</p></div>
        </div>
        <div className="tutorial-editor-header-actions">
          {definition.sourcePdfUrl && (
            <button className="btn btn-secondary" onClick={() => setShowPdf(true)}><FileText size={15} /> View PDF</button>
          )}
          <span className="badge badge-warning">Draft</span>
        </div>
      </div>
      {showPdf && definition.sourcePdfUrl && <PdfPreviewModal title={definition.title} url={definition.sourcePdfUrl} onClose={() => setShowPdf(false)} />}
      {error && <div className="tutorial-editor-error">{error}</div>}
      {saveMessage && <div className="tutorial-editor-success"><Check size={16} /> {saveMessage}</div>}
      <section className="card tutorial-editor-card">
        <div className="tutorial-editor-section-heading"><div><h3>Tutorial details</h3><p>These identifiers are used by the desktop app.</p></div></div>
        <div className="tutorial-editor-fields">
          <label>Title<input value={title} onChange={(event) => setTitle(event.target.value)} /></label>
          <label>Application<input value={appName} onChange={(event) => setAppName(event.target.value)} /></label>
          <label>Tutorial key<input value={tutorialKey} onChange={(event) => setTutorialKey(event.target.value)} /></label>
        </div>
      </section>
      <section className="card tutorial-editor-card">
        <div className="tutorial-editor-section-heading"><div><h3>Steps</h3><p>Reorder, refine, or remove generated steps. Subtitle steps keep their position in the lesson video.</p></div><button className="btn btn-secondary" onClick={() => setSteps((current) => [...current, { instruction: '', targetText: '', images: [] }])}><Plus size={15} /> Add step</button></div>
        <div className="editor-steps">
          {steps.map((step, index) => (
            <article className="editor-step" key={`step-${index}`}>
              <div className="editor-step-heading"><strong>Step {index + 1}</strong>{step.startTime !== undefined && step.endTime !== undefined && <span className="field-help">Video {formatVideoTime(step.startTime)}–{formatVideoTime(step.endTime)}</span>}<div className="editor-step-actions"><button className="icon-btn" onClick={() => moveStep(index, -1)} disabled={index === 0} aria-label={`Move step ${index + 1} up`} title="Move up"><ArrowUp size={15} /></button><button className="icon-btn" onClick={() => moveStep(index, 1)} disabled={index === steps.length - 1} aria-label={`Move step ${index + 1} down`} title="Move down"><ArrowDown size={15} /></button><button className="icon-btn danger" onClick={() => setSteps((current) => current.filter((_, stepIndex) => stepIndex !== index))} disabled={steps.length === 1} aria-label={`Remove step ${index + 1}`} title="Remove step"><Trash2 size={15} /></button></div></div>
              <label>Instruction<textarea value={step.instruction} onChange={(event) => updateStep(index, { instruction: event.target.value })} rows={3} /></label>
              <label>Target metadata <span className="field-help">Optional JSON object</span><textarea className="target-input" value={step.targetText} onChange={(event) => updateStep(index, { targetText: event.target.value })} rows={4} placeholder={'{"role":"button","name":"Save"}'} /></label>
              {step.images.length > 0 && <div className="editor-step-images"><span>Step images from the PDF</span><div>{step.images.map((image) => <img key={`${image.pageNumber}-${image.name}`} src={image.dataUrl} alt={`Source PDF page ${image.pageNumber}`} />)}</div></div>}
            </article>
          ))}
        </div>
      </section>
      <div className="tutorial-editor-footer"><Link className="btn btn-secondary" to="/tutorials">Cancel</Link><button className="btn btn-primary" onClick={() => void saveDraft()} disabled={saving || steps.length === 0}>{saving ? 'Saving...' : 'Save draft'}</button></div>
    </div>
  );
}
